import React from "react";
import {
  CloseOutlined,
  LoadingOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import ImageOutline from "./ImageOutline";

const DEFAULT_ZOOM_STEP = 0.3;

const getXY = (e: any) => {
  let x = 0;
  let y = 0;
  if (e.touches && e.touches.length) {
    x = e.touches[0].pageX;
    y = e.touches[0].pageY;
  } else {
    x = e.pageX;
    y = e.pageY;
  }
  return { x, y };
};

const Action = (props: any) => {
  if (!props.condition) return null;
  return <React.Fragment>{props.children}</React.Fragment>;
};

export default class ImageGallery extends React.Component<any> {
  initX = 0;
  initY = 0;
  lastX = 0;
  lastY = 0;
  state = {
    x: 0,
    y: 0,
    zoom: 1,
    rotate: 0,
    loading: true,
    moving: false,
    current: (this.props?.startIndex as any) ?? 0,
  };
  createTransform = (x: number, y: number, zoom: number, rotate: number) =>
    `translate3d(${x}px,${y}px,0px) scale(${zoom}) rotate(${rotate}deg)`;
  stopSideEffect = (e: any) => e.stopPropagation();
  getCurrentImage = (p: any) => p.image ?? "";
  getCurrentTitle = (p: any) => p.title ?? "";
  getCurrentOutline = (p: any) => p.outline ?? 0;
  resetZoom = () => {
    this.setState({ x: 0, y: 0, zoom: 1 });
    this.initX = 0;
    this.initY = 0;
    this.lastX = 0;
    this.lastY = 0;
  };
  startMove = (e: any) => {
    this.setState({ moving: true });
    const xy = getXY(e);
    this.initX = xy.x - this.lastX;
    this.initY = xy.y - this.lastY;
  };
  duringMove = (e: any) => {
    if (!this.state.moving) return false;
    const xy = getXY(e);
    this.lastX = xy.x - this.initX;
    this.lastY = xy.y - this.initY;
    this.setState({
      x: xy.x - this.initX,
      y: xy.y - this.initY,
    });
  };
  endMove = () => {
    this.setState({ moving: false });
    if (this.state.zoom <= 1) this.resetZoom();
  };
  applyZoom = (type: string) => {
    const { zoomStep = DEFAULT_ZOOM_STEP } = this.props;
    switch (type) {
      case "in":
        this.setState({ zoom: this.state.zoom + zoomStep });
        break;
      case "out":
        const newZoom = this.state.zoom - zoomStep;
        if (newZoom < 1) break;
        else if (newZoom === 1) this.resetZoom();
        else this.setState({ zoom: newZoom });
        break;
    }
  };
  applyRotate = (type: string) => {
    switch (type) {
      case "cw":
        this.setState({ rotate: this.state.rotate + 90 });
        break;
      case "acw":
        this.setState({ rotate: this.state.rotate - 90 });
        break;
    }
  };
  reset = (e: any) => {
    this.stopSideEffect(e);
    this.setState({ x: 0, y: 0, zoom: 1, rotate: 0 });
  };
  exit = (e: any) => {
    if (typeof this.props.onClose === "function") {
      const container = document.querySelector(
        ".image-gallery-canvas"
      ) as HTMLElement;
      container.classList.add("zoomOut");
      setTimeout(() => {
        this.props.onClose(e);
      }, 100);
    }
  };
  canvasClick = (e: any) => {
    const { clickOutsideToExit = true } = this.props;
    if (clickOutsideToExit) return this.exit(e);
  };
  keyboardNavigation = (e: any) => {
    const { allowZoom = true } = this.props;
    const { y, zoom } = this.state;
    switch (e.key) {
      case "ArrowUp":
        if (zoom > 1) this.setState({ y: y + 20 });
        break;
      case "ArrowDown":
        if (zoom > 1) this.setState({ y: y - 20 });
        break;
      case "+":
        if (allowZoom) this.applyZoom("in");
        break;
      case "-":
        if (allowZoom) this.applyZoom("out");
        break;
      case "Escape":
        this.exit(e);
        break;
    }
  };
  componentDidMount() {
    document.body.classList.add("image-gallery-open");
    const modal = document.querySelector(".modal-wrap") as HTMLElement;
    modal.classList.add("modal-blur");
    const { keyboardInteraction = true } = this.props;
    if (keyboardInteraction) {
      document.addEventListener("keyup", this.keyboardNavigation);
    }
  }
  componentWillUnmount() {
    document.body.classList.remove("image-gallery-open");
    const modal = document.querySelector(".modal-wrap") as HTMLElement;
    modal.classList.remove("modal-blur");
    const { keyboardInteraction = true } = this.props;
    if (keyboardInteraction) {
      document.removeEventListener("keyup", this.keyboardNavigation);
    }
  }
  render() {
    const image = this.getCurrentImage(this.props);
    const title = this.getCurrentTitle(this.props);
    const outline: string | undefined = this.getCurrentOutline(this.props);
    if (!image) {
      return null;
    }
    const {
      allowZoom = true,
      // allowRotate = true,
      buttonAlign = "flex-end",
      showTitle = true,
    } = this.props;
    const { x, y, zoom, rotate, loading, moving } = this.state;
    return (
      <div className="image-gallery-container">
        <div className="image-gallery-mask"></div>
        <div
          className="image-gallery-header"
          style={{ justifyContent: buttonAlign }}
        >
          <Action condition={showTitle && title}>
            <div
              className="image-gallery-title"
              style={{
                display: buttonAlign === "center" ? "none" : "flex",
                order: buttonAlign === "flex-start" ? 2 : "unset",
              }}
            >
              <span
                title={title}
                style={{
                  textAlign: buttonAlign === "flex-start" ? "right" : "left",
                }}
              >
                {title}
              </span>
            </div>
          </Action>
          <Action condition={allowZoom}>
            <ZoomInOutlined
              className="image-gallery-button"
              onClick={() => this.applyZoom("in")}
            />
            <ZoomOutOutlined
              className={`image-gallery-button ${
                zoom <= 1 ? "image-gallery-disabled" : ""
              }`}
              onClick={() => this.applyZoom("out")}
            />
          </Action>
          {/* <Action condition={allowRotate}>
            <RotateLeftOutlined
              className="image-gallery-button"
              onClick={() => this.applyRotate("acw")}
            />
            <RotateRightOutlined
              className="image-gallery-button"
              onClick={() => this.applyRotate("cw")}
            />
          </Action> */}
          <CloseOutlined
            className="image-gallery-button"
            onClick={(e) => this.exit(e)}
          />
        </div>
        <div
          className="image-gallery-canvas"
          onClick={(e) => this.canvasClick(e)}
        >
          {loading && (
            <LoadingOutlined
              style={{
                fontSize: 28,
                position: "fixed",
                color: "#0071e3",
                margin: "auto",
              }}
            />
          )}
          <div
            className="image-container"
            style={{
              transform: this.createTransform(x, y, zoom, rotate),
              cursor: zoom > 1 ? "grab" : "unset",
              transition: moving ? "none" : "ease-in-out all 0.2s",
            }}
          >
            <ImageOutline outline={outline} />
            <img
              draggable="false"
              onMouseDown={(e) => this.startMove(e)}
              onTouchStart={(e) => this.startMove(e)}
              onMouseMove={(e) => this.duringMove(e)}
              onTouchMove={(e) => this.duringMove(e)}
              onMouseUp={() => this.endMove()}
              onMouseLeave={() => this.endMove()}
              onTouchEnd={() => this.endMove()}
              onClick={(e) => this.stopSideEffect(e)}
              onLoad={() => this.setState({ loading: false })}
              className={`image-preview image-gallery-img${
                loading ? " image-gallery-loading" : ""
              }`}
              title={title}
              src={image}
              alt={title}
            />
          </div>
        </div>
      </div>
    );
  }
}
